/**
 * Public Terms of Service — reachable without a session at /terms.
 *
 * Plain-English B2B SaaS terms. Intentionally not bilingual: the binding
 * contract with the customer is the MSA / order form the customer's
 * counsel reviewed; this page is the public-facing default set of terms
 * for click-through signups and is written in one language (English) to
 * avoid translation drift between versions.
 */
import type { ReactNode } from "react";
import { LegalLayout } from "./LegalLayout";

const EFFECTIVE_DATE = "April 17, 2026";

export function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      effectiveDate={EFFECTIVE_DATE}
      sibling={{ to: "/privacy", label: "Read the Privacy Policy →" }}
    >
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your company&apos;s use
        of the Lenavio HR platform (the &ldquo;Service&rdquo;) provided by
        Lenavio, Inc. (&ldquo;Lenavio,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;), a company
        incorporated in the United States with its principal place of
        business in Florida. By creating an account or using the Service
        you (&ldquo;Customer&rdquo;) agree to these Terms on behalf of the
        organization you represent, and you confirm you have authority to
        do so.
      </p>
      <p>
        If your organization has signed a separate Master Services
        Agreement or order form with us, that agreement controls to the
        extent it conflicts with these Terms.
      </p>

      <Section title="1. Controller / processor relationship">
        <p>
          Customer is the <strong>data controller</strong> of personal data
          uploaded to the Service. Lenavio is the <strong>data processor</strong>
          and handles that data only on Customer&apos;s documented instructions
          and to operate the Service. Customer owns its data and the
          outputs the Service generates for it. Lenavio does not sell or
          rent Customer data, does not share it with third parties for
          their own marketing, and does not use it for any purpose other
          than providing, securing, and improving the Service for that
          Customer. Our Privacy Policy and Data Processing Addendum
          further describe these commitments.
        </p>
      </Section>

      <Section title="2. Account responsibilities">
        <ul>
          <li>
            Customer is responsible for the accounts created under its
            tenant, for inviting and removing users, and for keeping role
            assignments up to date.
          </li>
          <li>
            Customer must keep authentication credentials confidential and
            notify us promptly at{" "}
            <a className="text-accent hover:underline" href="mailto:security@lenavio.com">
              security@lenavio.com
            </a>{" "}
            if it suspects unauthorized access.
          </li>
          <li>
            Customer is responsible for the lawfulness of the data it
            uploads, for informing its employees about the processing, and
            for obtaining any consents its local law requires.
          </li>
        </ul>
      </Section>

      <Section title="3. Acceptable use">
        <p>Customer and its users agree not to:</p>
        <ul>
          <li>Use the Service to violate applicable law, infringe third-party rights, or engage in discriminatory conduct.</li>
          <li>Upload malware, exploit vulnerabilities, or attempt to gain unauthorized access to other tenants or to Lenavio&apos;s infrastructure.</li>
          <li>Reverse-engineer the Service, resell it, or build a competing product from it.</li>
          <li>Use the Service to send unsolicited marketing or spam.</li>
          <li>
            Rely on AI-assisted features to make employment decisions,
            disciplinary actions, or any other adverse recommendations
            without meaningful human review. See Section 7.
          </li>
        </ul>
      </Section>

      <Section title="4. Subscriptions, billing, and Stripe">
        <p>
          Paid plans are billed through Stripe, Inc. Customer agrees to
          Stripe&apos;s payment terms for the payment method it provides. Plans
          renew automatically at the end of each billing period (monthly
          or annual, depending on the plan selected) until cancelled.
        </p>
        <p>
          If an invoice fails or a payment is not received when due:
        </p>
        <ul>
          <li>
            <strong>Grace period (15 days).</strong> The account remains fully
            usable; we will retry the charge and send payment reminders.
          </li>
          <li>
            <strong>Read-only (day 16 through day 30).</strong> After 15 days
            past due, the account is restricted to read-only mode — users
            can view and export data but not create or edit it.
          </li>
          <li>
            <strong>Deactivation (day 31 through day 60).</strong> After 30
            days past due, the account is deactivated and users cannot log
            in. Data is preserved during this window.
          </li>
          <li>
            <strong>Deletion (after day 60).</strong> If the account has not
            been reactivated within 30 days of deactivation, we
            permanently delete tenant data from our production systems.
            Encrypted backups are rotated out on their normal schedule.
          </li>
        </ul>
        <p>
          Fees are non-refundable except where required by law. Taxes are
          the responsibility of the Customer unless we are legally
          required to collect them.
        </p>
      </Section>

      <Section title="5. Uptime and support">
        <p>
          We target <strong>99.9% monthly uptime</strong> for the Service,
          measured excluding scheduled maintenance and factors outside our
          reasonable control. This target is a commitment to run the
          Service reliably; it is <strong>not</strong> a contractual SLA with
          service credits on the free tier or the Starter plan. Service
          credits and formal SLAs are available on Professional and
          Enterprise plans as described in the applicable order form.
        </p>
        <p>
          Support is offered by email during business hours. Response-time
          targets depend on plan level and, where applicable, the order
          form.
        </p>
      </Section>

      <Section title="6. Termination and data export">
        <p>
          Either party may terminate these Terms by giving{" "}
          <strong>30 days&apos; written notice</strong> to the other (email to{" "}
          <a className="text-accent hover:underline" href="mailto:legal@lenavio.com">
            legal@lenavio.com
          </a>{" "}
          is sufficient on the Customer side). We may suspend or terminate
          the Service immediately if Customer materially breaches these
          Terms and fails to cure within a reasonable period, or if
          continued operation would expose us or other customers to
          material legal or security risk.
        </p>
        <p>
          Upon termination, Customer may export its data in a
          machine-readable format for <strong>30 days</strong>. After that, we
          delete the data as described in Section 4.
        </p>
      </Section>

      <Section title="7. AI-assisted features — human-in-the-loop required">
        <p>
          The Service includes AI-assisted features (drafting, summaries,
          question answering, pattern detection). These features are
          intended to help human decision-makers work faster. They are
          <strong> not </strong>a substitute for human judgment, and they are
          not infallible: outputs can be incomplete, inaccurate, or
          biased.
        </p>
        <p>
          <strong>
            AI never makes employment decisions, disciplinary actions, or
            adverse recommendations on its own.
          </strong>{" "}
          Hiring, firing, promotion, compensation, discipline, performance
          ratings, and similar decisions must be made by authorized
          humans who review the underlying facts. Customer is responsible
          for how its people act on AI output and for any resulting
          decisions.
        </p>
        <p>
          Data processed by AI features is covered by the enterprise
          data-protection terms we maintain with our AI provider
          (Anthropic) and is <strong>not</strong> used to train foundation
          models.
        </p>
      </Section>

      <Section title="8. Intellectual property">
        <p>
          Customer owns its data and the reports, exports, and outputs
          generated on its behalf. Lenavio owns the Service, its software,
          designs, and documentation. Customer grants Lenavio a limited
          licence to host, process, and display its data solely to
          operate the Service for Customer. Feedback that Customer
          voluntarily provides about the Service may be used by us to
          improve the product, without obligation.
        </p>
      </Section>

      <Section title="9. Warranties and disclaimers">
        <p>
          We will provide the Service with reasonable skill and care. To
          the maximum extent permitted by law, and except as expressly
          stated in these Terms or in an applicable order form, the
          Service is provided <strong>&ldquo;as is&rdquo;</strong> without warranties of
          merchantability, fitness for a particular purpose, or
          non-infringement. We do not warrant that the Service will be
          uninterrupted or error-free, or that AI-assisted outputs will
          be accurate or suitable for any specific purpose.
        </p>
      </Section>

      <Section title="10. Limitation of liability">
        <p>
          To the maximum extent permitted by law, each party&apos;s total
          aggregate liability under or in connection with these Terms
          will not exceed the total fees paid by Customer to Lenavio in
          the <strong>twelve (12) months</strong> immediately preceding the event
          giving rise to the claim. Neither party will be liable for
          indirect, incidental, special, consequential, or punitive
          damages, or for lost profits, lost revenue, or lost data,
          however caused.
        </p>
        <p>
          These limits do not apply to a party&apos;s indemnification
          obligations, breach of confidentiality, infringement of the
          other party&apos;s intellectual property, or liability that cannot
          be excluded under applicable law.
        </p>
      </Section>

      <Section title="11. Indemnification">
        <p>
          Customer will defend and indemnify Lenavio against third-party
          claims arising out of (a) Customer data that infringes a third
          party&apos;s rights or violates applicable law, or (b) Customer&apos;s
          use of the Service in breach of these Terms. Lenavio will
          defend and indemnify Customer against third-party claims that
          the Service, used as permitted, infringes that third party&apos;s
          intellectual property rights, subject to the liability limits
          in Section 10.
        </p>
      </Section>

      <Section title="12. Confidentiality">
        <p>
          Each party will protect the other&apos;s confidential information
          using at least the same care it uses for its own, and will not
          disclose it except to employees and contractors who need to
          know and are bound by equivalent confidentiality obligations.
          This clause does not restrict disclosures required by law,
          provided the receiving party gives reasonable notice where
          permitted.
        </p>
      </Section>

      <Section title="13. Governing law and dispute resolution">
        <p>
          These Terms are governed by the laws of the State of Florida,
          United States, without regard to its conflict-of-laws rules.
          The parties will first attempt to resolve any dispute through
          good-faith negotiation.
        </p>
        <p>
          If a dispute is not resolved within 30 days, it will be settled
          by <strong>binding arbitration</strong> administered by the American
          Arbitration Association under its Commercial Arbitration Rules,
          seated in <strong>Palm Beach County, Florida</strong>. Judgment on the
          award may be entered in any court of competent jurisdiction.
          Notwithstanding the foregoing, either party may seek injunctive
          or equitable relief in court to protect its intellectual
          property or confidential information.
        </p>
      </Section>

      <Section title="14. Changes to these Terms">
        <p>
          We may update these Terms from time to time. If we make{" "}
          <strong>material changes</strong>, we will notify tenant administrators
          by email at least <strong>30 days</strong> before the changes take
          effect. Continued use of the Service after the effective date
          constitutes acceptance of the updated Terms. Non-material
          changes (typos, formatting, clarifications) may be made at any
          time.
        </p>
      </Section>

      <Section title="15. Miscellaneous">
        <ul>
          <li><strong>Entire agreement.</strong> These Terms, together with the Privacy Policy and any signed order form or MSA, are the entire agreement between the parties regarding the Service.</li>
          <li><strong>Assignment.</strong> Neither party may assign these Terms without the other&apos;s consent, except to an affiliate or in connection with a merger, acquisition, or sale of substantially all of its assets.</li>
          <li><strong>Severability.</strong> If any provision is held unenforceable, the rest of the Terms remain in effect.</li>
          <li><strong>No waiver.</strong> A failure to enforce a right is not a waiver of that right.</li>
          <li><strong>Force majeure.</strong> Neither party is liable for delays caused by events beyond its reasonable control.</li>
        </ul>
      </Section>

      <Section title="16. Contact">
        <p>
          Legal notices and questions about these Terms go to{" "}
          <a className="text-accent hover:underline" href="mailto:legal@lenavio.com">
            legal@lenavio.com
          </a>
          . Written correspondence can be addressed to Lenavio, Inc.,
          Florida, United States.
        </p>
      </Section>
    </LegalLayout>
  );
}

// ── Small layout helpers ──────────────────────────────────────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold font-heading text-text-primary pt-4">
        {title}
      </h2>
      <div className="space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_a]:break-words">
        {children}
      </div>
    </section>
  );
}
